import { Injectable, Injector } from '@angular/core';
import { WorkOrderControllerService } from '@app/generated/service';
import { MaterialDto } from '@app/generated/model';
import { WorkOrderMaterial } from './work-order-material.model';
import { AbstractWorkOrderDataService } from '@app/work-order/abstract-work-order-data.service';
import { map } from 'rxjs/operators';
import { firstValueFrom } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class WorkOrderMaterialService extends AbstractWorkOrderDataService<WorkOrderMaterial[]> {
  constructor(
    private workOrderControllerService: WorkOrderControllerService,
    injector: Injector,
  ) {
    super(injector, 'MATERIAL');
  }

  protected rehydrate(): void {
    // Keep backward compatibility with previously persisted type keys.
    this.offlineData.workOrderDataItems
      .where({ workOrderId: this.workOrderId, type: 'MATERIAL' })
      .first()
      .then(
        (item) =>
          item ??
          this.offlineData.workOrderDataItems.where({ workOrderId: this.workOrderId, type: 'MATERIALS' }).first(),
      )
      .then((item) => {
        if (!item?.data) {
          this.setData([]);
          return;
        }

        const materials = this.normalizeMaterials(item.data);
        this.setData(materials);
      })
      .catch((error) => {
        console.warn(`[MATERIAL] Failed to rehydrate materials for ${this.workOrderId}`, error);
        this.setData([]);
      });
  }

  protected load(): void {
    this.workOrderControllerService
      .getWorkOrderMaterialList(this.workOrderId)
      .pipe(
        map((dtos) => {
          return dtos.map((dto) => WorkOrderMaterial.fromDto(dto));
        }),
      )
      .subscribe({
        next: (data) => this.setData(data),
        error: (error) => {
          console.warn(`[MATERIAL] Failed to load materials for ${this.workOrderId}.`, error);
          void this.handleLoadFailure([], error);
        },
      });
  }

  public async download() {
    try {
      const dtos = await firstValueFrom(this.workOrderControllerService.getWorkOrderMaterialList(this.workOrderId));
      const materials = (dtos || []).map((dto) => WorkOrderMaterial.fromDto(dto));
      this.setData(materials);
    } catch (error) {
      console.warn('[MATERIAL] Failed to load materials for offline download. Saving current/empty snapshot.', error);
      const current = this.get();
      this.setData(Array.isArray(current) ? current : []);
    }

    await super.download();
  }

  private normalizeMaterials(payload: unknown): WorkOrderMaterial[] {
    if (!Array.isArray(payload)) {
      return [];
    }

    return payload.map((item) => {
      const material = item as WorkOrderMaterial;
      // Already in client model shape
      if (material?.materialId || material?.materialNumber) {
        return new WorkOrderMaterial(material);
      }

      // Legacy payload shape persisted as MaterialDto[]
      return WorkOrderMaterial.fromDto(item as MaterialDto);
    });
  }
}
